import { ICustomer } from './customer/customer.interface';
import { Item, IItemSummary } from './items/items.interface';
import { IPromo } from './items/promo.interface';
import { ISurcharge } from './items/surcharge.interface';
import { ITip } from './items/tip.interface';
import { Subject, Observable, timer } from 'rxjs';

import has from 'lodash/has';
import forOwn from 'lodash/forOwn';
import size from 'lodash/size';
import filter from 'lodash/filter';
import each from 'lodash/each';
import map from 'lodash/map';
import findIndex from 'lodash/findIndex';
import isPlainObject from 'lodash/isPlainObject';
import values from 'lodash/values';
import find from 'lodash/find';
import { Bump_screen, IBumpScreenContext, IBumpScreenLocation } from './bump-screen.model';

import * as moment from 'moment';
import { takeUntil } from 'rxjs/operators';
import { IPrintResponse } from '../api/core/printer/printer.interface';
import { WatcherService } from '../api/utils/watcher/watcher.service';
import { APP_DOCKET_DISPLAY_UPDATE } from '../api/utils/watcher/watcher-event';
import {
  BffDispatchRequest,
  BffOrderDispatchBumpStatus,
  BffOrderDispatchDate,
} from '../api/core/docket/dispatchers/order-dispatcher/order-dispatcher.service';
import { UtilityClass } from '../api/utils/UtilityClass';

export type OrderType = 'single_order' | 'batch_order';

export type FoodDrinkType = 'food' | 'drink';

export enum CONTEXT_TYPE {
  Delivery = 'delivery',
  Pickup = 'onlinepickup',
  Dine_in = 'dinein',
  Room_service = 'room_service',
  Drive_up = 'drive_up',
}

export interface IBundleOrder {
  customer: ICustomer;
  orders: IBundleCustomerOrder[];
}

export interface IBundleCustomerOrder {
  id: number;
  comment: string;
  items: Item[];
  tips: ITip[];
  promos: IPromo[];
  surcharges: ISurcharge[];
}

export class Order {
  bump_status: string;
  comments:
    | string
    | {
        [id: string]: {
          customer_ref: string;
          message: string;
          customer: string;
        };
      };
  context_title: string;
  context_type: string;
  created_time: string;
  suggested_time: string;
  eta: string;
  fraud_risk: string;
  id: string;
  line_items: Item[] = [];
  paid: boolean;
  payment_method: string;
  requested_time: string;
  requested_time_end: string;
  seq_no: number;
  receipt: string[] = [];
  total: number = 0;
  customer: ICustomer;
  location_id: string;

  // BATCH ORDERING
  batch_customers: ICustomer[];
  split_receipt: { [id: number]: string[] };
  order_ids: any[];
  batch_order_bundle: IBundleOrder[] = [];
  type: OrderType = 'single_order';

  // ROOM SERVICE
  room_number: string;

  // DRIVE UP
  car_rego: string;
  car_colour: string;

  // O@T
  private _tableLabel: string;
  table_name: string;
  table_id: number | string;

  // C@C
  is_collect_at_counter: boolean = false;
  collect_at_counter_sms: string;
  collect_at_counter_sms_food: string;
  collect_at_counter_sms_drink: string;

  // DELIVERY
  is_delivery_window: boolean = false;

  // SYNCING
  last_updated_time: number;

  // DOCKET DISPLAY
  docket_details_display_label: string;
  docket_display_label: string;
  docket_display_summary: string;

  // POLL v2
  promos: IPromo[] = [];
  surcharges: ISurcharge[] = [];
  tips: ITip[] = [];

  // PREORDER
  auto_set_eta: boolean = false;
  printed: boolean = true;

  // NEW ORDER ANIMATION
  pristine: boolean = false;

  // PENDING STATE
  private _pendingValueInterval;
  private _pendingDuration: number = 3000;
  pendingValuePercentage: number = 0;
  pending: boolean = false;
  pendingChanged: Subject<boolean> = new Subject();
  pendingProgressChanged: Subject<number> = new Subject();

  // FILTERING
  filter_module: string = '';
  filter_state: string = '';

  // REFUND
  refunded: boolean = false;
  refunded_amt: number;

  //VIP
  public vipChanged: Subject<boolean> = new Subject();
  private _vip: boolean = false;

  public get vip(): boolean {
    return this._vip;
  }

  public set vip(v: boolean) {
    this._vip = v;
    this.vipChanged.next(v);
  }

  // PRINTER GROUPS
  split_dockets: {
    [key: number]: string[];
  };

  // PRINTER ERROR HANDLING
  private _printerErrors: IPrintResponse[] = [];
  public printerErrorUpdate: Subject<IPrintResponse[]> = new Subject();

  // OUTPUT ERROR HANDLING
  failed_output: boolean = false;
  failed_output_reason: string;

  constructor(docketObject: any) {
    this.bump_status = docketObject.bump_status;
    this.comments = docketObject.comments;
    this.context_title = docketObject.context_title;
    this.context_type = docketObject.context_type;
    this.created_time = docketObject.created_time;
    this.suggested_time = docketObject.suggested_time;
    this.eta = docketObject.eta;
    this.fraud_risk = docketObject.fraud_risk;
    this.id = docketObject.id;
    this.paid = docketObject.paid;
    this.payment_method = docketObject.payment_method;
    this.requested_time = docketObject.requested_time;
    this.requested_time_end = docketObject.requested_time_end;
    this.receipt = docketObject.receipt;
    this.split_receipt = docketObject.split_receipt;
    this.total = +docketObject.total;
    this.split_dockets = docketObject.split_dockets;
    this.location_id = docketObject.location_id;
    this.table_name = docketObject.table_name != null ? docketObject.table_name : '';
    this.room_number = docketObject.room_number || docketObject.room_name;
    this.last_updated_time = docketObject.last_updated_time ? moment(docketObject.last_updated_time).valueOf() : null;
    this.auto_set_eta = !!docketObject.auto_set_eta;
    this.printed = !!docketObject.printed;
    this.refunded = !!docketObject.refunded;
    this.refunded_amt = docketObject.refunded_amt ? docketObject.refunded_amt : 0;

    // collect at counter
    this.is_collect_at_counter = !!docketObject.is_collect_at_counter;
    this.collect_at_counter_sms = docketObject.collect_at_counter_sms;
    this.collect_at_counter_sms_food = docketObject.collect_at_counter_sms_food;
    this.collect_at_counter_sms_drink = docketObject.collect_at_counter_sms_drink;

    // filter module smarts
    if (this.context_type == CONTEXT_TYPE.Dine_in) {
      this.filter_module = this.is_collect_at_counter ? 'collect_at_counter' : CONTEXT_TYPE.Dine_in;
    } else {
      this.is_collect_at_counter = false;
      this.filter_module = this.context_type;
    }

    // delivery logic
    this.is_delivery_window = size(this.requested_time_end) > 0;

    // room service name cleanup
    this.room_number = this.room_number == 'Not set' ? null : this.room_number;

    this.car_rego = docketObject.car_rego ? docketObject.car_rego.toUpperCase() : '';
    this.car_colour = docketObject.car_colour || '';

    this.table_id = docketObject.table_id;
    this.order_ids = docketObject.order_ids;
    this.type = docketObject.type || 'single_order';

    // line items cleanup - remove surcharge, promos and tips
    this.line_items = this._cleanup_line_items(docketObject.line_items);
    this.promos = docketObject.promos;
    this.surcharges = docketObject.surcharge;
    this.tips = docketObject.tips;

    //outupt error
    this.failed_output = docketObject.failed_output;
    this.failed_output_reason = docketObject.failed_output_reason || "POS Error";

    // clone the customer
    let main_customer: any = { ...docketObject.customer };
    delete main_customer.ext_customers;

    // now store the parent customer
    this.customer = main_customer;

    // FOR BATCH ORDERS
    if (this.type == 'batch_order') {
      // create the batch customers
      this.batch_customers = [];
      this.batch_customers.push(main_customer);
      if (has(docketObject.customer, 'ext_customers') && size(docketObject.customer.ext_customers)) {
        forOwn(docketObject.customer.ext_customers, (value: ICustomer, key: string) => {
          this.batch_customers.push(value);
        });
      }
    }

    // convert object to array
    if (isPlainObject(docketObject.line_items)) {
      this.line_items = values(docketObject.line_items);
    }

    const items: number = this.getNumberOfItems();
    const itemsLabel: string = items == 1 ? 'item' : 'items';

    switch (this.context_type) {
      case CONTEXT_TYPE.Delivery:
        this.docket_details_display_label = `Delivery to ${this.customer.address.suburb} for ${this.customer.first_name} ${this.customer.last_name}`;
        this.docket_display_label = this.customer.address.suburb;
        this.docket_display_summary = `${items} item delivery for ${this.customer.first_name}`;
        break;
      case CONTEXT_TYPE.Pickup:
        this.docket_details_display_label = `Pickup for ${this.customer.first_name} ${this.customer.last_name}`;
        this.docket_display_label = `${this.customer.first_name} ${this.customer.last_name}`;
        this.docket_display_summary = `${items} ${itemsLabel} for pickup`;
        break;
      case CONTEXT_TYPE.Dine_in:
        if (this.is_collect_at_counter) {
          this.docket_details_display_label = `Collection for ${this.customer.first_name} : ${this.customer.phone}`;
          this.docket_display_label = `${this.customer.first_name}`;
          this.docket_display_summary = `Order <b>#${this.id}<b>`;
        } else {
          let order_table_name: string = this.customer.first_name;
          if (this.type == 'batch_order') {
            // get all first names
            let names: string[] = map(this.batch_customers, (customer: ICustomer) => customer.first_name);
            let last: string;
            if (size(names) > 1) {
              last = names.pop();
              order_table_name = names.join(', ');
              order_table_name += ' & ' + last;
            }
          }

          this.docket_details_display_label = 'Table ' + this.getTableLabel() + ` for ${this.customer.first_name}`;
          this.docket_display_label = 'Table ' + this.getTableLabel();
          this.docket_display_summary = `${items} ${itemsLabel} for ${order_table_name}`;
        }
        break;
      case CONTEXT_TYPE.Room_service:
        this.docket_details_display_label = `Room service for ${this.customer.first_name} ${this.customer.last_name}`;
        this.docket_display_label = 'Room ' + this.room_number;
        this.docket_display_summary = `${items} item room service for ${this.customer.first_name}`;
        break;
      case CONTEXT_TYPE.Drive_up:
        this.docket_details_display_label = `Drive-up for ${this.car_rego}(${this.car_colour})`;
        this.docket_display_label = `${this.car_rego} (${this.car_colour})`;
        this.docket_display_summary = `${items} item drive-up for ${this.customer.first_name}`;
        break;
    }

    if (this.type == 'batch_order') {
      each(this.batch_customers, (customer: ICustomer) => {
        let batchBundle: IBundleOrder = {
          customer: customer,
          orders: [],
        };

        batchBundle.orders = this.getAllCustomerOrders(customer.phone, docketObject.line_items);
        this.batch_order_bundle.push(batchBundle);
      });
    }
  }

  private _cleanup_line_items(line_items: any[]): Item[] {
    let retVal: Item[] = [];
    each(line_items, (item: any) => {
      if (size(item.type)) {
        if (item.type == 'surcharge' || item.type == 'coupon' || item.type == 'tip') {
          // ignore - we now have a surcharge property
        } else {
          // could be an ITEM or COMBO
          retVal.push(item);
        }
      } else {
        // ignore - we now have a promo property
      }
    });

    return retVal;
  }

  getTableLabel(): string {
    if (this._tableLabel) {
      return this._tableLabel;
    }

    let tableName: string = '';
    if (size(this.table_name)) tableName = this.table_name;
    else tableName = this.table_id + '';

    if (size(this.room_number)) {
      tableName = tableName + ' (' + this.room_number + ')';
    }

    this._tableLabel = tableName;
    return tableName;
  }

  getComments(id?: string): any {
    if (size(this.comments)) {
      if (this.type == 'single_order') {
        return this.comments;
      } else {
        return this.comments[id];
      }
    }
  }

  getUserComments(phoneNumber: string): any[] {
    let retVal: any[] = [];

    forOwn(this.comments, comment => {
      if (comment.customer_ref == phoneNumber) retVal.push(comment);
    });

    return retVal;
  }

  getUserOrderId(phoneNumber: string, returnType: 'text' | 'list' = 'text'): string | string[] {
    let ids: string[] = map(
      filter(this.order_ids, order => {
        return order.customer_ref == phoneNumber;
      }),
      'order_id',
    );

    if (returnType === 'text') {
      return size(ids) ? ids.join(', ') : '';
    } else {
      return ids;
    }
  }

  getAllCustomerOrders(phoneNumber: string, line_items: any[]): IBundleCustomerOrder[] {
    let retVal: IBundleCustomerOrder[] = [];

    const orderIds: string[] = this.getUserOrderId(phoneNumber, 'list') as string[];
    each(orderIds, id => {
      let order: IBundleCustomerOrder = {
        id: id,
        comment: '',
        items: [],
        surcharges: [],
        promos: [],
        tips: [],
      };

      // add the comment
      if (has(this.comments, id) && size(this.comments[id].message)) {
        order.comment = this.comments[id].message;
      }

      // now add the line items
      let items: Item[] = filter(line_items, ['order_id', id]);
      order.items = this._cleanup_line_items(items);

      // get the surcharges
      order.surcharges = filter(this.surcharges, ['order_id', id]);

      // get the promos
      order.promos = filter(this.promos, ['order_id', id]);

      // get the tips
      order.tips = filter(this.tips, ['order_id', id]);

      // add to list
      retVal.push(order);
    });

    return retVal;
  }

  public getNumberOfItems(): number {
    let retVal: number = 0;

    for (let item of this.line_items) {
      if (item.type != 'item' && item.items && item.items.length > 0) {
        for (let i of item.items) {
          if (i.type == 'item') {
            retVal += has(item, 'qty') ? +item.qty : 1;
          }
        }
      } else if (item.type == 'item') {
        retVal += has(item, 'qty') ? +item.qty : 1;
      }
    }

    return retVal;
  }

  /**
   * Checks if food/drink split is enabled for this order's context
   * @param activeBumpScreen - The active bump screen containing location and context details
   * @returns true if food_drink_split is enabled for this order's context, false otherwise
   */
  public isFoodDrinkSplitEnabled(activeBumpScreen: Bump_screen): boolean {
    try {
      if (!this.is_collect_at_counter || !activeBumpScreen?.location_details || !this.location_id) {
        return false;
      }

      const location: IBumpScreenLocation = find(activeBumpScreen.location_details, {
        id: this.location_id,
      });

      const context: IBumpScreenContext = location
        ? find(location.contexts, {
            context_type: this.context_type,
          })
        : null;

      return context?.food_drink_split === true;
    } catch {
      return false;
    }
  }

  public getOrderSummary(): IItemSummary[] {
    let summary: IItemSummary[] = [];
    for (let item of this.line_items) {
      // if combo
      if (item.type != 'item' && item.items && item.items.length > 1) {
        for (let i of item.items) {
          if (i.type == 'item') {
            // check if portion combo
            const index: number = findIndex(summary, { id: i.id });
            const qty: number = has(item, 'qty') ? +item.qty : 1;

            if (index == -1) {
              summary.push({
                id: i.id,
                title: i.title,
                qty: item.type == 'portion_combo' ? 1 / item.items.length : qty,
              });
            } else {
              summary[index].qty += item.type == 'portion_combo' ? 1 / item.items.length : qty;
            }
          }
        }
      } else if (item.type == 'item') {
        const index: number = findIndex(summary, { id: item.id });
        const qty: number = has(item, 'qty') ? +item.qty : 1;
        if (index == -1) {
          summary.push({
            id: item.id,
            title: item.title,
            qty: qty,
          });
        } else {
          summary[index].qty += qty;
        }
      }
    }

    return summary;
  }

  public setPending(pending: boolean): void {
    this.pending = pending;
    this.pendingChanged.next(this.pending);

    clearInterval(this._pendingValueInterval);

    if (pending) {
      const buffTime: number = this._pendingDuration / 1000 / 100;
      let pendingValue: number = 0;
      this._pendingValueInterval = setInterval(() => {
        if (pendingValue > 1) {
          clearInterval(this._pendingValueInterval);
          this.setPending(false);
          WatcherService.broadcastStatic(APP_DOCKET_DISPLAY_UPDATE, 'orderPendingFinished');
        }

        pendingValue += buffTime;
        this.pendingValuePercentage = pendingValue;
        this.pendingProgressChanged.next(pendingValue);
      }, this._pendingDuration * buffTime);
    } else {
      this.pendingValuePercentage = 0;
    }
  }

  sendSMSProgress(minutesToSend: number, timezone: string, stopSchedule: Subject<boolean>): Observable<number> {
    const smsSubject: Subject<number> = new Subject();
    const stop$: Subject<boolean> = stopSchedule;

    const secondsMax = minutesToSend * 60;
    const requestedTimeMoment = UtilityClass.parseTimeToTimezone(this.requested_time, timezone);
    const createdTimeMoment = UtilityClass.parseTimeToTimezone(this.created_time, timezone);
    const orderDiff = createdTimeMoment.diff(requestedTimeMoment, 'minutes');

    // if ordered more than an hour
    let targetTime: moment.Moment;
    if (orderDiff > 60) {
      targetTime = moment(requestedTimeMoment).add(minutesToSend, 'minutes');
      targetTime = targetTime.subtract(60, 'minutes');
    } else {
      targetTime = moment(createdTimeMoment).add(minutesToSend, 'minutes');
    }

    timer(0, 10000)
      .pipe(takeUntil(stop$))
      .subscribe(
        data => {
          const now = UtilityClass.momentNow(timezone);
          const timeLeft = Math.min(secondsMax, targetTime.diff(now, 'seconds'));
          const smsValue = Math.min(1, (secondsMax - timeLeft) / secondsMax);
          smsSubject.next(smsValue);

          // to display counts
          if (smsValue >= 1) {
            stop$.next(true);
          }
        },
        () => {
          console.log('ORDER TIMER STOPPED');
        },
        () => {
          // console.log("TIMER COMPLETE", this.id);
        },
      );

    return smsSubject;
  }

  public getDateSortReference(): number {
    if (this.context_type == CONTEXT_TYPE.Dine_in) {
      return new Date(this.created_time).valueOf();
    } else if (this.is_delivery_window) {
      return new Date(this.requested_time ? this.requested_time : this.created_time).valueOf();
    }

    return new Date(this.eta ? this.eta : this.requested_time ? this.requested_time : this.created_time).valueOf();
  }

  updatePrinterErrors(errors: IPrintResponse[]) {
    // clear printer errors
    this._printerErrors = errors || [];
    this.printerErrorUpdate.next(this._printerErrors);
  }

  /**
   * Check if the order is already acknowledged by the user.
   *
   * Considered ACKed IF (AND)
   *
   * 1. NOT PENDING (from setting ETA or Printed value)
   * 2. Has ETA
   * 3. AUTO SET ETA and PRINTED and ordered TODAY
   * 4. STATUS = BUMPED
   */
  isAcknowledged(): boolean {
    if (this.bump_status == 'bumped') return true; // #4 STATUS = BUMPED

    return (
      !this.pending && // #1 NOT PENDING
      this.eta && // #2 HAS ETA
      (!this.auto_set_eta || // #3
        this.printed ||
        this.requested_time.substring(0, 10) != this.created_time.substring(0, 10))
    );
  }

  orderFilterBumpState(): 'new' | 'bumped' | 'current' {
    if (this.bump_status == 'bumped') {
      return 'bumped';
    } else {
      return this.isAcknowledged() ? 'current' : 'new';
    }
  }

  getDispatchValue(request: BffDispatchRequest): BffOrderDispatchDate | BffOrderDispatchBumpStatus | boolean {
    switch (request) {
      case BffDispatchRequest.BUMP_STATUS:
        return <BffOrderDispatchBumpStatus>this.bump_status;
      case BffDispatchRequest.ETA:
        return <BffOrderDispatchDate>this.eta;
      case BffDispatchRequest.PRINTED:
        return this.printed;
      case BffDispatchRequest.SMS:
        return <BffOrderDispatchDate>this.collect_at_counter_sms;
    }
  }

  public get Printer_errors(): IPrintResponse[] {
    return this._printerErrors || [];
  }
}
